import { useEffect, useRef, useState } from 'react';

import {
  PopupDropdown,
  PopupDropdownItem,
  PopupDropdownScroll,
  PopupSelect,
  PopupSelectInput,
  PopupSelectWrap,
} from './styles';
import { TIME_OPTIONS_15, snapToOption } from './timePicker';

const ClockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    style={{ width: 24, height: 24, flexShrink: 0 }}
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

type Props = {
  value: string;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  // Always receives a value already snapped to the 15-min grid.
  onChange: (label: string) => void;
  ariaLabel: string;
};

// Shared start/end time control used by both the inline ScheduleStep picker and
// the ReviewStep ShiftPopup. Admins can pick from the dropdown or type a custom
// time (snapped to the nearest 15-min slot). When the dropdown opens it scrolls
// the current value into view instead of anchoring at 12:00 AM.
const TimeField = ({
  value,
  open,
  onOpen,
  onClose,
  onChange,
  ariaLabel,
}: Props) => {
  const [draft, setDraft] = useState(value);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mirror external changes (e.g. end-time auto-advance) into the input when the
  // user isn't mid-edit. `value` is always a canonical grid label.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Bring the selected option into view each time the dropdown opens.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView?.({ block: 'center' });
  }, [open]);

  const commit = () => {
    if (draft === value) return; // nothing typed — don't re-fire onChange
    const snapped = snapToOption(draft);
    if (snapped && snapped !== value) {
      onChange(snapped);
    } else {
      setDraft(value); // unparseable or same slot — revert the text
    }
  };

  return (
    <PopupSelectWrap>
      <PopupSelect onClick={onOpen}>
        <PopupSelectInput
          aria-label={ariaLabel}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={onOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              onClose();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === 'Escape') {
              setDraft(value);
              onClose();
            }
          }}
          onBlur={() => {
            commit();
            onClose();
          }}
        />
        <ClockIcon />
      </PopupSelect>
      {open ? (
        <PopupDropdown>
          <PopupDropdownScroll ref={scrollRef}>
            {TIME_OPTIONS_15.map((t) => (
              <PopupDropdownItem
                key={t}
                data-selected={t === value ? 'true' : undefined}
                // mousedown (not click) so selection wins the race against the
                // input's blur-commit.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(t);
                }}
              >
                {t}
              </PopupDropdownItem>
            ))}
          </PopupDropdownScroll>
        </PopupDropdown>
      ) : null}
    </PopupSelectWrap>
  );
};

export default TimeField;
